// File: js/supabaseClient.js — Supabase client construction.
//
// This module used to read `process.env` at MODULE SCOPE and, when the keys were
// missing, append an error box to `document.body` and throw. Two consequences:
//
//   * importing it was impossible outside a browser, so every module that touched
//     it became untestable — `db/transport.js` says so in its own header and takes
//     the client as a parameter for exactly this reason;
//   * the failure mode was a side effect of an import, which is the one place a
//     caller cannot catch it.
//
// Now: configuration is EXPLICIT (`createSupabaseClient(config)`), the DOM is
// touched only when a caller asks (`renderConfigError(document)`), and the
// long-standing `supabase` export is preserved — but built LAZILY, on first use,
// so importing this file has no side effect at all.

import { createClient } from '@supabase/supabase-js';

/**
 * @typedef {Object} SupabaseConfig
 * @property {string} url
 * @property {string} anonKey
 */

/**
 * Read the build-time configuration esbuild injects (`define` in
 * `esbuild.config.js` replaces these two expressions with string literals).
 *
 * Takes the environment as an argument so a test can supply its own without
 * mutating `process.env`. Returns `null` fields rather than throwing: the caller
 * decides what an unconfigured build should look like.
 *
 * @param {Record<string, string|undefined>} [env]
 * @returns {{url:string|null, anonKey:string|null}}
 */
export function readEnvConfig(env) {
    const source = env || {
        SUPABASE_URL: process.env.SUPABASE_URL,
        SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY
    };
    const url = source.SUPABASE_URL || null;
    const anonKey = source.SUPABASE_ANON_KEY || null;
    return { url: url || null, anonKey: anonKey || null };
}

/**
 * Build a Supabase client from explicit configuration.
 *
 * @param {{url:string|null, anonKey:string|null}} config
 * @param {{createClient?: Function, options?: object}} [deps] Injectable factory.
 * @returns {import('@supabase/supabase-js').SupabaseClient}
 * @throws {Error} With `code = 'supabase-config-missing'` when either key is
 *   absent. A named, catchable error — not an error box written into `document`.
 */
export function createSupabaseClient(config, deps = {}) {
    const factory = deps.createClient || createClient;
    const url = config && config.url;
    const anonKey = config && config.anonKey;
    if (!url || !anonKey) {
        const error = new Error(
            'Supabase configuration missing: set SUPABASE_URL and SUPABASE_ANON_KEY in .env and rebuild.'
        );
        // @ts-ignore — a stable machine code, same discipline as db/errors.js.
        error.code = 'supabase-config-missing';
        throw error;
    }
    // Auth options EXPLICIT rather than inherited defaults, because two of them
    // are load-bearing for the idle-tablet failure ("tout charge indéfiniment
    // après une pause") : persistSession + autoRefreshToken keep the session
    // renewable across the one-hour JWT expiry, and app.js restarts the
    // refresher on tab wake-up (visibilitychange) because browsers throttle the
    // timers it relies on while a tab is idle or the machine sleeps.
    // detectSessionInUrl is off: password login only — nothing arrives via URL
    // fragments, and parsing them is one more thing that can touch the hash,
    // which since 48c83fc carries the CURRENT PANE.
    return factory(url, anonKey, deps.options || {
        auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: false
        }
    });
}

/**
 * Show the build-misconfiguration banner.
 *
 * Called by `ui/engine-boot.js` when construction fails — never at import time.
 * `doc` is a parameter so this is testable and so the module never assumes a DOM.
 *
 * @param {Document} [doc]
 * @returns {boolean} Whether a banner was actually rendered.
 */
export function renderConfigError(doc) {
    const target = doc || (typeof document !== 'undefined' ? document : null);
    if (!target || !target.body) return false;
    if (target.getElementById && target.getElementById('supabase-config-error')) return true;

    const box = target.createElement('div');
    box.id = 'supabase-config-error';
    box.className = 'alert alert-danger';
    box.setAttribute('role', 'alert');
    box.style.cssText = 'position:fixed;left:1rem;right:1rem;top:1rem;z-index:9999;';

    const content = target.createElement('div');
    content.className = 'alert-content';

    const title = target.createElement('div');
    title.className = 'alert-title';
    title.textContent = 'Configuration manquante';

    const message = target.createElement('div');
    message.className = 'alert-message';
    // textContent, never innerHTML (audit M7).
    message.textContent =
        'La connexion au serveur n\'est pas configurée (SUPABASE_URL / SUPABASE_ANON_KEY). '
        + 'Aucun encaissement ne peut être enregistré — utilisez le carnet de reçus papier et prévenez l\'administrateur.';

    content.appendChild(title);
    content.appendChild(message);
    box.appendChild(content);
    target.body.appendChild(box);
    return true;
}

/** @type {import('@supabase/supabase-js').SupabaseClient|null} */
let cached = null;

/**
 * The application's single Supabase client, built on first use.
 *
 * @param {{url:string|null, anonKey:string|null}} [config] Override, for tests.
 * @returns {import('@supabase/supabase-js').SupabaseClient}
 */
export function getSupabaseClient(config) {
    if (config) {
        cached = createSupabaseClient(config);
        return cached;
    }
    if (!cached) cached = createSupabaseClient(readEnvConfig());
    return cached;
}

/**
 * The historical `supabase` export, preserved for every module that already
 * imports it.
 *
 * It is a lazy proxy: the client is constructed on the FIRST property access, not
 * at import. That is the whole difference — an unconfigured build now fails where
 * a caller can catch it, instead of throwing out of an import and taking the
 * bundle with it.
 */
export const supabase = new Proxy(/** @type {any} */ ({}), {
    get(_target, prop) {
        const client = getSupabaseClient();
        const value = /** @type {any} */ (client)[prop];
        return typeof value === 'function' ? value.bind(client) : value;
    },
    set(_target, prop, value) {
        /** @type {any} */ (getSupabaseClient())[prop] = value;
        return true;
    },
    has(_target, prop) {
        return prop in /** @type {any} */ (getSupabaseClient());
    }
});
